import axios from 'axios';
import { unauthorizedInterceptors } from "../utilities/functions";

// Create a dedicated API instance for mediaplan endpoints
const mediaplanAPI = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add unauthorized interceptors
unauthorizedInterceptors(mediaplanAPI);

// Set auth header helper function
const setAuthHeader = () => {
  const token = localStorage.getItem('access_token');
  if (token) {
    mediaplanAPI.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
};

/**
 * Get notification count for the bell icon
 * @returns {Promise<number>} - The count of pending notifications
 */
export const getNotificationCount = async () => {
  try {
    setAuthHeader();
    const response = await mediaplanAPI.get('/mediaplan/api/notification/count/');
    return response.data.count;
  } catch (error) {
    console.error('Error fetching notification count:', error);
    return 0;
  }
};

/**
 * Get today's actions
 * @returns {Promise<Array>} - List of today's actions
 */
export const getTodayActions = async () => {
  try {
    setAuthHeader();
    const response = await mediaplanAPI.get('/mediaplan/api/actions/', {
      params: { date_filter: 'today' }
    });
    console.log(response.data, " response.data");
    return response.data;
  } catch (error) {
    console.error('Error fetching today actions:', error);
    return [];
  }
};

/**
 * Get action history (completed or canceled actions)
 * @returns {Promise<Array>} - List of historical actions
 */
export const getActionHistory = async () => {
  try {
    setAuthHeader();
    const response = await mediaplanAPI.get('/mediaplan/api/actions/', {
      params: { date_filter: 'history' }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching action history:', error);
    return [];
  }
};

/**
 * Complete an action
 * @param {number} actionId - ID of the action to complete
 * @returns {Promise<Object>} - The updated action
 */
export const completeAction = async (actionId) => {
  try {
    setAuthHeader();
    const response = await mediaplanAPI.post(`/mediaplan/api/actions/${actionId}/complete/`);
    return response.data;
  } catch (error) {
    console.error('Error completing action:', error);
    throw error;
  }
};

/**
 * Snooze an action
 * @param {number} actionId - ID of the action to snooze
 * @param {string} snoozeUntil - ISO date string for when to snooze until
 * @returns {Promise<Object>} - The updated action
 */
export const snoozeAction = async (actionId, snoozeUntil) => {
  try {
    setAuthHeader();
    const response = await mediaplanAPI.post(`/mediaplan/api/actions/${actionId}/snooze/`, {
      snooze_until: snoozeUntil
    });
    return response.data;
  } catch (error) {
    console.error('Error snoozing action:', error);
    throw error;
  }
};

/**
 * Cancel an action
 * @param {number} actionId - ID of the action to cancel
 * @returns {Promise<Object>} - The updated action
 */
export const cancelAction = async (actionId) => {
  try {
    setAuthHeader();
    const response = await mediaplanAPI.post(`/mediaplan/api/actions/${actionId}/cancel/`);
    return response.data;
  } catch (error) {
    console.error('Error canceling action:', error);
    throw error;
  }
};

/**
 * Reactivate a snoozed or canceled action
 * @param {number} actionId - ID of the action to reactivate
 * @returns {Promise<Object>} - The updated action
 */
export const reactivateAction = async (actionId) => {
  try {
    setAuthHeader();
    const response = await mediaplanAPI.post(`/mediaplan/api/actions/${actionId}/reactivate/`);
    return response.data;
  } catch (error) {
    console.error('Error reactivating action:', error);
    throw error;
  }
};

/**
 * Clear all today's actions (mark as completed/dismissed)
 * @returns {Promise<Object>} - Result of the operation
 */
export const clearAllTodayActions = async () => {
  try {
    setAuthHeader();
    const response = await mediaplanAPI.post('/mediaplan/api/actions/clear-all/');
    return response.data;
  } catch (error) {
    console.error('Error clearing all actions:', error);
    throw error;
  }
};

/**
 * Get action history with optional month filter
 * @param {string} month - Optional month filter in YYYY-MM format
 * @returns {Promise<Array>} - List of historical actions
 */
export const getActionHistoryByMonth = async (month = null) => {
  try {
    setAuthHeader();
    const params = { date_filter: 'history' };
    if (month) {
      params.month = month;
    }
    const response = await mediaplanAPI.get('/mediaplan/api/actions/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching action history:', error);
    return [];
  }
};
